import React, { useContext, useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { separateThreeDigitsWithCommas } from 'services/financial';
import { onRequestError } from 'services/handlers';

import { cardAssignmentStatusMap, paymentTypeMap } from 'components/pages/patient-source/Patients/constants';

import { PrepaidApi, StudyApi } from '../../../../../../../api';
import Button from '../../../../../../../common/general/Button';
import { NSPP_CARD } from '../../../../../../../services/i18n/namespaces';
import { PatientInfoContext } from '../../PatientInfoContext';

import './CardBalance.scss';

export default function CardBalance() {
  const { t } = useTranslation();
  const { patientInfo, updatePatientInfo } = useContext(PatientInfoContext);
  const cardId = patientInfo.cardInfo?.id;
  const paymentType = patientInfo.cardInfo?.paymentType;
  const cardAssignmentStatus = patientInfo.cardAssignmentStatus;
  const isCardIdRegistered = patientInfo.cardInfo?.registered;
  const patientId = patientInfo.id;

  const [loading, setLoading] = useState(false);
  const [show, setShow] = useState(false);
  const [cardBalance, setCardBalance] = useState(null);
  const [statusMsg, setStatusMsg] = useState(null);
  const buttonRef = useRef(null);
  const wrapperRef = useRef(null);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
    function handleClickOutside(event) {
      if (buttonRef.current && buttonRef.current.contains(event.target)) {
        return;
      }
      if (wrapperRef.current && wrapperRef.current.contains(event.target)) {
        return;
      }
      setShow(false);
    }
  }, [wrapperRef, buttonRef]);

  const resolveCardType = () => {
    if (!paymentType && cardAssignmentStatus) {
      return t(`${NSPP_CARD}:statuses.${cardAssignmentStatusMap[cardAssignmentStatus]}`);
    } else if (paymentType) {
      return t(`${NSPP_CARD}:statuses.${paymentTypeMap[paymentType]}`);
    } else {
      return t(`${NSPP_CARD}:statuses.na`);
    }
  };

  return (
    <>
      <div
        className="eds-card-balance"
        onClick={e => {
          e.stopPropagation();
          e.nativeEvent.stopImmediatePropagation();
        }}
      >
        <span>{t(`${NSPP_CARD}:cardBalance`)} </span>
        <Button
          size="h28"
          priority="low"
          ref={buttonRef}
          disabled={!cardId}
          title={!cardId ? t(`${NSPP_CARD}:pidNotDefined`) : t(`${NSPP_CARD}:showCardBalance`)}
          onClick={() => {
            if (!show) {
              loadBalance();
            }
            setShow(!show);
          }}
        >
          {t('show')}
        </Button>
        {show && (
          <div ref={wrapperRef} className="eds-card-balance-tooltip">
            {getCardBalance()}
          </div>
        )}
      </div>
      <div className="eds-card-type">
        <Trans i18nKey="cardType" ns={NSPP_CARD} values={{ value: resolveCardType() }} />
      </div>
    </>
  );

  function getCardBalance() {
    if (loading) {
      return (
        <span className="spinner-border" role="status">
          <span className="sr-only">Loading...</span>
        </span>
      );
    }

    if (isCardIdRegistered && cardBalance) {
      return `$ ${separateThreeDigitsWithCommas(cardBalance)}`;
    }

    if (isCardIdRegistered && statusMsg) {
      return statusMsg;
    }

    return (
      <React.Fragment>
        {t(`${NSPP_CARD}:cardIsNotRegistered`)}{' '}
        <Button
          size="h28"
          priority="low"
          onClick={() => {
            updateCardStatus(cardId, patientInfo.id);
          }}
          className="ml-2"
        >
          {t('retry')}
        </Button>
      </React.Fragment>
    );
  }

  function updateCardStatus(proxyNumber, patientUniqueIdentifier) {
    setLoading(true);
    StudyApi.retryCard(proxyNumber, patientUniqueIdentifier, true)
      .then(() => {
        updatePatientInfo();
      }, onRequestError)
      .finally(function() {
        setLoading(false);
      });
  }

  function loadBalance() {
    if (!isCardIdRegistered) {
      return;
    }
    setLoading(true);
    setCardBalance(null);
    PrepaidApi.getCardBalanceByProxy(cardId, patientId, true)
      .then(function({ data: cardBalance }) {
        setCardBalance(cardBalance.toLocaleString());
      })
      .catch(function() {
        setStatusMsg('Error while receiving card balance, please try again later');
      })
      .finally(function() {
        setLoading(false);
      });
  }
}
